/* eslint-disable no-console */
import castArray from 'lodash/castArray';
import findIndex from 'lodash/findIndex';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import reduce from 'lodash/reduce';
import remove from 'lodash/remove';
import { buffers, channel } from 'redux-saga';
import { call, delay, fork, put, putResolve, take, takeEvery } from 'redux-saga/effects';

import * as DebugSettingsUtil from '../lib/configuration/DebugSettingsUtil';
import MissingUtilError from '../lib/error/MissingUtilError';
import TaskManagerError from '../lib/error/TaskManagerError';
import { tryCall } from '../sagas/Helpers';
import { removeSpawnedTasksInGroup } from '../sagas/Helpers/SpawnedTaskManager';
import Logger from '../tools/Logger';

let mappings = {};
const HANDLED = Symbol('Handled');
let id = 0;
const LOOP = true;

const log = Logger.child({ component: 'sagas/TaskManager' });

/*
 * This method registers a handler to be executing when an action comes through.
 * actionType: the string type of the action, or an array of strings
 * handler: a generator that can be called with the action as an argument
 * concur: one of [takeOnly, takeOnce, takeEvery, takeLatest] which will determine
 * how the handler will handle several of the same actions at once. concur can
 * also be a function, e.g. the memoized takeMaxNumTaskMemoizer concur function
 * below.
 */
export function registerHandler({ actionType, handler, concur = 'takeOnce', wait } = {}) {
  const actionTypes = castArray(actionType);
  const registeredHandlers = [];
  id++;

  actionTypes.forEach(_actionType => {
    if (isNil(_actionType)) {
      throw new Error('actionType in taskManager handler cannot be null or undefined');
    }
    if (!mappings[_actionType]) {
      mappings[_actionType] = [];
    }

    const existingMappingIdx = findIndex(mappings[_actionType], mapping => {
      return mapping.handler === handler;
    });

    // we want to make sure to not register the same handler again for the
    // action
    if (mappings[_actionType].length && existingMappingIdx !== -1) return;

    const handlerMap = {
      handler,
      concur,
      // wait used for the takeOnlyWithWait and takeOnlyDebounce concurrency
      wait,
      id,
      actionTypes,
      executing: false,
      childProcess: null,
    };
    registeredHandlers.push({
      actionType: _actionType,
      handlerId: id,
    });
    mappings[_actionType].push(handlerMap);
  });

  return registeredHandlers;
}

export function registerHandlers(handlers = []) {
  let registeredHandlers = [];
  handlers.forEach((handler, index) => {
    try {
      registeredHandlers = registeredHandlers.concat(registerHandler(handler));
    } catch (e) {
      throw new Error(`unable to registerHandler ${JSON.stringify(handler)} at index ${index}`);
    }
  });
  return registeredHandlers;
}

export function removeHandler(actionType, handlerId) {
  if (!(mappings[actionType] && mappings[actionType].length)) return;

  return remove(mappings[actionType], handler => {
    if (handler.id === handlerId) {
      if (handler.childProcess) {
        handler.childProcess.cancel();
      }
      return true;
    }
    return false;
  });
}

export function removeAndStopHandlers(handlers, group) {
  handlers.forEach(handler => {
    removeHandler(handler.actionType, handler.handlerId);
  });
  removeSpawnedTasksInGroup(group);
}

export function removeHandlersByActionTypes(actionTypes, handlerId) {
  return reduce(
    actionTypes,
    (results, actionType) => {
      return results.concat(removeHandler(actionType, handlerId));
    },
    [],
  );
}

export function removeAllHandlers() {
  mappings = {};
}

// This is only intended for tests to consume externally
export function getMappingsByType(actionType) {
  return mappings[actionType];
}

// Concurrency utils

export function getTakeLatestFinallyCallback(mappingVal, childPid) {
  return () => {
    if (mappingVal.executing && childPid && childPid === mappingVal.executing) {
      mappingVal.executing = false;
      mappingVal.childProcess = null;
    }
  };
}

function takeLatestReturn(mapping, childProcess, childPid) {
  return new Promise((res, rej) => {
    childProcess
      .toPromise()
      .then(res)
      .catch(rej)
      .then(getTakeLatestFinallyCallback(mapping, childPid));
  });
}

// Concurrency solutions
/*
  concur: String

  - takeEvery: every time the action comes in pass to the handler,
  while letting the previous handler execute. Use for things like analytics events
  where you want to emit every action that comes through without dropping any.

  - takeLatest: take every action and cancel any previously executing handlers
  if they are still executing when the next action comes in. Use for things like
  autocomplete where you would want to cancel the previous when new actions come in.

  - takeOnly: take action and pass to handler and ignore any actions that come
  in while the handler is still executing. Use in situations where you want to debounce
  the action as long as it takes for the handler to complete when changing stations.

  - takeOnlyDebounce: same as takeOnly but you can specify an additional wait
  after taking one action. We don't want to wait for the handler to complete before
  accepting new actions.

  - takeOnlyWithWait: same as takeOnly but you can specify an additional wait after
  the handler has completed to ignore actions. Use when you want to debounce extra
  beyond the time it takes the handler.

  - takeOnce: take the action once and delegate to the handler, then stop listening. Use
  for an analytics action that only needs to fire once on an action.

  - throttle: takes an action every (n) milliseconds ignoring intermediary actions while storing
  the last action to be processed at completion

  concur: Function

  takeMaxNumTaskMemoizer: the return value of this function can be used as a concurrency pattern
  that allows for taking some list of actions only X number of times.
 */

function* takeEveryTask(mapping, action) {
  mapping.executing = true;
  try {
    yield call(mapping.handler, action);
  } finally {
    // eslint-disable-next-line require-atomic-updates
    mapping.executing = false;
  }
}
/*
  For takeLatest we need to make sure if the handler could be spawned by
  multiple actions that we cancel any executing processes. For example if action A
  spawned worker 1, if action B came in and was part of the same mapping (actionType: [action A, action B], handler: worker 1)
  we would want to cancel worker 1 even though action B didn't spawn it
*/
export function cancelAnyChildHandlers(mapping) {
  const mappingId = mapping.id;
  mapping.actionTypes.forEach(mappingTypes => {
    const handlerToCancel = mappings[mappingTypes].find(handler => {
      return handler.id === mappingId;
    });
    if (handlerToCancel && handlerToCancel.childProcess && handlerToCancel.executing) {
      handlerToCancel.childProcess.cancel();
      handlerToCancel.childProcess = null;
    }
  });
}

export function* takeLatestTask(mapping, action) {
  yield call(cancelAnyChildHandlers, mapping);
  const childProcess = yield fork(mapping.handler, action);
  // eslint-disable-next-line require-atomic-updates
  mapping.childProcess = childProcess;
  // Save the executing thread id
  mapping.executing = (mapping.childProcess && mapping.childProcess.id) || 1;
  yield call(takeLatestReturn, mapping, childProcess, mapping.executing);
}

function* takeOnlyTask(mapping, action) {
  if (mapping.executing) return;
  mapping.executing = true;
  try {
    yield call(mapping.handler, action);
  } finally {
    // eslint-disable-next-line require-atomic-updates
    mapping.executing = false;
  }
}

function* throttleMonitor(mapping) {
  mapping.channel = yield call(channel, buffers.sliding(1));
  while (LOOP) {
    const payload = yield take(mapping.channel);
    yield call(mapping.handler, payload);
    yield delay(mapping.wait);
  }
}

function* throttle(mapping, action) {
  if (!mapping.channel) {
    yield fork(throttleMonitor, mapping);
  }
  yield put(mapping.channel, action);
}

function* takeOnlyDebounceTask(mapping, action) {
  if (mapping.executing) return;
  mapping.executing = true;
  try {
    yield fork(mapping.handler, action);
    yield delay(mapping.wait);
  } finally {
    // eslint-disable-next-line require-atomic-updates
    mapping.executing = false;
  }
}

function* takeOnlyWithWaitTask(mapping, action) {
  if (mapping.executing) return;
  mapping.executing = true;
  try {
    yield call(mapping.handler, action);
  } finally {
    if (mapping.wait) {
      yield delay(mapping.wait);
    }
    // eslint-disable-next-line require-atomic-updates
    mapping.executing = false;
  }
}

export function* takeOnceTask(mapping, action) {
  removeHandlersByActionTypes(mapping.actionTypes, mapping.id);
  yield call(mapping.handler, action);
}

// This is an example of a valid function that can be passed into the `concur`
// param of a handler config, though in this case it happens to be wrapped in a
// memoizer. Example use: `concur: takeMaxNumTaskMemoizer(2)`
export function takeMaxNumTaskMemoizer(maxNum) {
  let count = 0;
  return function* takeMaxNumTask(mapping, action) {
    count++;
    if (count >= maxNum) {
      removeHandlersByActionTypes(mapping.actionTypes, mapping.id);
    }
    yield call(mapping.handler, action);
  };
}

export const concurrencyNameToHandlerMap = {
  takeEvery: takeEveryTask,
  takeLatest: takeLatestTask,
  takeOnly: takeOnlyTask,
  takeOnlyDebounce: takeOnlyDebounceTask,
  takeOnlyWithWait: takeOnlyWithWaitTask,
  throttle,
};

/**
 *
 * @param {object} taskDefinition
 * @returns {Function}
 */
export function getConcurrencyHandler(taskDefinition) {
  if (isFunction(taskDefinition.concur)) {
    return taskDefinition.concur;
  }

  return concurrencyNameToHandlerMap[taskDefinition.concur] || takeOnceTask;
}

/**
 *
 * @param {object} taskDefinition - object containing the following { concur, id, handler }
 * @param {object} action
 * @param {Array} errors
 */
export function* startActionHandler(taskDefinition, action, errors) {
  const concurrencyFn = getConcurrencyHandler(taskDefinition);

  try {
    yield call(concurrencyFn, taskDefinition, action);
  } catch (error) {
    if (error instanceof MissingUtilError) {
      log.error(
        `Missing util: ${
          taskDefinition && taskDefinition.handler && taskDefinition.handler.name
        } needs ${error.util}`,
      );
    } else {
      errors.push(error);
    }
  }
}

/**
 *
 * @param {object} action
 * @returns {Array}
 */
export function* startAllActionHandlers(action) {
  const actionType = action.type;
  const errors = [];
  // We need to make a copy in case somehow a handler modifies the array of handlers
  // for the action. For example, a takeOnly handler removes itself after its work is complete,
  // so we would want to make sure that that removal won't cause us to not iterate through a subsequent
  // handler.
  const mappingsCopy = mappings[actionType].concat();

  for (let i = 0; i < mappingsCopy.length; i++) {
    const taskDefinition = mappingsCopy[i];

    if (!taskDefinition.handler) {
      log.error(`startAllActionHandler(): ${action.type} is being handled by undefined handler`);
    } else {
      log.trace(
        `startAllActionHandler(): ${taskDefinition.handler.name} handling action ${action.type}`,
      );
    }

    // Fork off new sagas for each action handler and aggregate errors so they can be processed higher up
    yield fork(startActionHandler, taskDefinition, action, errors);
  }

  return errors;
}

let groupDepth = 0;

function addGroup(name) {
  ++groupDepth;
  console.group(name);
}

function removeGroup() {
  --groupDepth;
  console.groupEnd();
}

/**
 *
 * @param {object} action
 */
export function* handleAction(action) {
  const errors = yield call(startAllActionHandlers, action);

  if (errors.length) {
    throw new TaskManagerError(
      `Handlers for ${action.type} threw ${errors.length} error(s): ${errors}`,
      errors,
    );
  }
}

export function loggedHandleAction(handler, isGroup = true) {
  return function* (action) {
    const actions = DebugSettingsUtil.actionGroupLoggingFilter();
    if (!actions.includes(action.type)) {
      console.time(action.type);
      if (isGroup) {
        addGroup(`Handling Action :: ${action.type}`);
        console.log('Action: ', action);
      } else {
        console.log(`%cPut Action :: ${action.type}`, 'font-weight: bold');
      }
    }
    try {
      yield call(handler, action);
    } finally {
      if (!actions.includes(action.type) && isGroup) {
        removeGroup();
        console.timeEnd(action.type);
      }
    }
  };
}

export function* putAndWait(actionOrActionType) {
  let action = actionOrActionType;
  if (typeof actionOrActionType === 'string') {
    action = { type: actionOrActionType };
  }
  action[HANDLED] = true;
  yield putResolve(action);
  if (mappings[action.type] && mappings[action.type].length) {
    yield call(
      DebugSettingsUtil.actionGroupLoggingEnabled()
        ? loggedHandleAction(handleAction)
        : handleAction,
      action,
    );
  }
}

function* taskWorker(action) {
  if (action[HANDLED]) return;
  // No need to delegate to handleAction if we already know we have no actions
  if (!(mappings[action.type] && mappings[action.type].length)) return;

  yield tryCall(
    DebugSettingsUtil.actionGroupLoggingEnabled()
      ? loggedHandleAction(handleAction, groupDepth === 0)
      : handleAction,
    action,
  );
}

export default function* taskManager() {
  yield takeEvery('*', taskWorker);
}
